import { DOCS_FORM_STATUS } from "core/consts";
import aiApi from "core/model/api/endpoints/extractDocs";
import { getErrorMessage } from "core/model/utils/errors";
import { DocsFormName, DocsFormStatusResponse } from "core/types";
import { progress } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";

const POLL_INTERVAL_MS = 2_000;

export const useDocsFillForm = ({
  careseekerId,
  onError,
  onPending,
  onSuccess,
  patientId,
  skip,
}: {
  careseekerId: number | undefined;
  patientId: number;
  skip: boolean;
  onSuccess: (response: DocsFormStatusResponse) => void;
  onError: (error: string) => void;
  onPending: () => void;
}) => {
  const [result, setResult] = useState<DocsFormStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, []);

  const clearPollTimeout = () => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  };

  const handleFormStatus = useCallback(
    (
      formResult: DocsFormStatusResponse,
      handlers: {
        onFilling?: () => void;
        onFilled: () => void;
        onFailed: (message: string) => void;
      },
    ) => {
      const { onFailed, onFilled, onFilling } = handlers;

      switch (formResult.status) {
        case DOCS_FORM_STATUS.PENDING:
        case DOCS_FORM_STATUS.FILLING:
          onFilling?.();
          break;
        case DOCS_FORM_STATUS.FILLED:
          onFilled();
          break;
        case DOCS_FORM_STATUS.FAILED:
          onFailed(formResult.error_message);
          break;
      }
    },
    [],
  );

  const fetchFormResult = useCallback(
    async (formName: DocsFormName): Promise<DocsFormStatusResponse | null> => {
      try {
        if (!careseekerId || !patientId) {
          throw new Error("careseekerId or patientId is not defined");
        }

        const formResult = await aiApi.docs.fill({
          careseekerId,
          formName,
          patientId,
        });

        if (!isMountedRef.current) return null;

        setResult(formResult);
        setError(null);
        return formResult;
      } catch (err) {
        if (!isMountedRef.current) return null;

        const message = getErrorMessage(err);
        setError(message);
        onError(message);
        clearPollTimeout();
        return null;
      }
    },
    [careseekerId, patientId, onError],
  );

  const startPolling = useCallback(
    (formName: DocsFormName) => {
      const poll = async () => {
        const formResult = await fetchFormResult(formName);
        if (!formResult || !isMountedRef.current) {
          return;
        }

        handleFormStatus(formResult, {
          onFailed: (message) => {
            onError(message);
            clearPollTimeout();
          },
          onFilled: () => {
            onSuccess(formResult);
            clearPollTimeout();
          },
          onFilling: () => {
            pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
          },
        });
      };

      poll();
    },
    [fetchFormResult, handleFormStatus, onError, onSuccess],
  );

  const fillFormAndPollForResult = useCallback(
    async (formName: DocsFormName) => {
      if (skip) {
        return null;
      }

      if (!careseekerId || !patientId) {
        onError("careseekerId or patientId is not defined");
        return null;
      }

      clearPollTimeout();
      onPending();

      if (result) {
        onSuccess(result);
        setError(null);
        return;
      }

      await aiApi.docs.start({
        careseekerId,
        formName,
        patientId,
      });

      const formResult = await fetchFormResult(formName);
      if (!formResult || !isMountedRef.current) return null;

      handleFormStatus(formResult, {
        onFailed: (message) => {
          onError(message);
        },
        onFilled: () => {
          onSuccess(formResult);
        },
        onFilling: () => {
          startPolling(formName);
        },
      });

      return null;
    },
    [
      careseekerId,
      patientId,
      skip,
      onError,
      onPending,
      onSuccess,
      fetchFormResult,
      startPolling,
      handleFormStatus,
    ],
  );

  return {
    error,
    fetchFormResult,
    fillForm: fillFormAndPollForResult,
    progress,
    resetResult: () => {
      setResult(null);
      setError(null);
    },
    result,
  };
};
